import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useLocalStorage } from 'usehooks-ts';
import { UsersIcon } from '@heroicons/react/24/outline';

import Navbar, { Breadcrumb } from '../components/Navbar/Navbar';
import Sidebar from '../components/Sidebar/Sidebar';
import AssetsService from '../services/AssetsService';
import useLocationRole from '../hooks/useLocationRole';

const SiteLayout = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const { pathname } = useLocation();
  const [, setLocationId] = useLocalStorage<number | undefined>('locationId', undefined);
  const { canManage } = useLocationRole(siteId);

  const { data: location } = useQuery({
    queryKey: ['location', siteId],
    queryFn: () => AssetsService.getLocation(Number(siteId)),
    enabled: !!siteId,
    select: (data) => data.location,
  });

  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: () => AssetsService.getLocations(),
    select: (data) => data.locations,
  });

  useEffect(() => {
    if (siteId) {
      setLocationId(Number(siteId));
    }
  }, [siteId, setLocationId]);

  const basePath = `/site/${siteId}`;
  const isSettings = pathname.endsWith('/settings');
  const isTeam = pathname.endsWith('/team');
  const isNewStation = pathname.endsWith('/station/new');
  const isStationRoute = pathname.includes('/station/') && !isNewStation;

  const currentPageLabel = isTeam ? 'Équipe' : isSettings ? 'Paramètres' : 'Stations';

  const siteDropdownOptions = useMemo(() => {
    return locations?.map((loc) => ({
      label: loc.name,
      href: `/site/${loc.id}`,
    })) ?? [];
  }, [locations]);

  const breadcrumbs = useMemo(() => {
    const hasMultipleSites = siteDropdownOptions.length > 1;
    const crumbs: Breadcrumb[] = [
      {
        label: location?.name ?? '...',
        href: basePath,
        icon: <img src="/site.svg" alt="" className="w-3.5 h-3.5 brightness-0 invert" />,
        ...(hasMultipleSites && { dropdownTitle: 'Sites', dropdownOptions: siteDropdownOptions }),
      },
    ];

    if (!isStationRoute && (isSettings || isTeam)) {
      crumbs.push({ label: currentPageLabel });
    }

    if (isNewStation) {
      crumbs.push({ label: 'Nouvelle station' });
    }

    return crumbs;
  }, [location?.name, basePath, currentPageLabel, isSettings, isTeam, isStationRoute, isNewStation, siteDropdownOptions]);

  const sidebarItems = useMemo(() => [
    {
      icon: <img src="/stations.svg" alt="" className="w-5 h-5" />,
      label: 'Stations',
      href: basePath,
      active: !isSettings && !isTeam && !isStationRoute && pathname === basePath,
    },
    // Only the site owner (or an admin) manages who else gets access.
    ...(canManage ? [{
      icon: <UsersIcon className="w-5 h-5 text-white-alpha-80" />,
      label: 'Équipe',
      href: `${basePath}/team`,
      active: isTeam && !isStationRoute,
    }] : []),
    {
      icon: <img src="/settings.svg" alt="" className="w-5 h-5" />,
      label: 'Paramètres',
      href: `${basePath}/settings`,
      active: isSettings && !isStationRoute,
    },
  ], [basePath, isSettings, isTeam, isStationRoute, pathname, canManage]);

  // When a station sub-route is active, skip SiteLayout's navbar/sidebar entirely.
  // StationLayout provides its own chrome (navbar + breadcrumbs + sidebar) for station pages.
  if (isStationRoute) {
    return <Outlet />;
  }

  return (
    <div className="flex flex-col h-screen bg-off-white">
      <Navbar
        breadcrumbs={breadcrumbs}
        sidebarItems={<Sidebar items={sidebarItems} isMobile />}
      />
      <div className="self-stretch flex-1 inline-flex justify-center items-start overflow-hidden">
        <Sidebar items={sidebarItems} isMobile={false} />
        <div className="flex-1 self-stretch pt-16 inline-flex flex-col justify-start items-center gap-8 overflow-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default SiteLayout;
